import React, { useState } from 'react';
import { Button, Form, message, Modal, Select, Tag } from 'antd';

import { useAddGovernanceFlagsAspectMutation } from '../../../../graphql/mutations.generated';
import { useTranslation } from '../../../../i18n/useTranslation';

type Props = {
    field?: any;
    entityData: any;
    onClose: () => void;
    refetch?: () => void;
};

/**
 * This component is used to add or remove governance flags for the entity profile page header section
 * @param entityData - entity data
 * @param onClose - function to close the modal
 * @param refetch - function to refetch the data
 * @param field - field data
 */
export const AddOrRemoveGovernanceFlagModal = ({ entityData, onClose, refetch, field }: Props) => {
    const { t } = useTranslation();
    const [updateAspectGovernance] = useAddGovernanceFlagsAspectMutation();
    const [isFormValid, setIsFormValid] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();

    // Close the modal and reset the form
    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    // Function to handle the form submit and update the governance flag
    const handleOk = async (formData: any) => {
        setIsLoading(true);
        const payload = {
            names: formData.names,
        };

        if (field) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            payload['subResource'] = field.fieldPath;
        }

        try {
            await updateAspectGovernance({
                variables: {
                    urn: entityData.urn,
                    input: payload,
                },
            });
            message.success({ content: t('Flag added successfully'), duration: 2 });
        } catch (e: unknown) {
            message.error({ content: t('Failed to add flag'), duration: 2 });
        } finally {
            setIsLoading(false);
        }
        refetch?.();
        handleClose();
    };

    // Function to handle the tag close event and remove the tag from the form
    const handleTagClose = (valueToRemove) => {
        const selectedValues = form.getFieldValue('names');
        const names = selectedValues.filter((value) => value !== valueToRemove);

        if (names.length === 0) {
            setIsFormValid(true);
        }
        form.setFieldsValue({ names });
    };

    return (
        <Modal
            title={t('Add Governance Flag')}
            visible
            className="f-modal"
            onCancel={handleClose}
            keyboard
            centered
            footer={
                <>
                    <Button
                        form="updateGovernanceFlagForm"
                        key="submit"
                        htmlType="submit"
                        type="primary"
                        disabled={isFormValid}
                        loading={isLoading}
                    >
                        {t('Add')}
                    </Button>
                    <Button onClick={handleClose}>{t('Cancel')}</Button>
                </>
            }
        >
            <Form
                form={form}
                name="updateGovernanceFlagForm"
                onFinish={handleOk}
                layout="vertical"
                onFieldsChange={() => {
                    const names = form.getFieldValue('names');
                    setIsFormValid(names.length === 0);
                }}
            >
                <Form.Item name="names" label={t('Select Flag')} required>
                    <Select
                        mode="multiple"
                        placeholder={t('Please select')}
                        aria-label={t('Select Flag')}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        options={[
                            { value: 'CDE', label: 'CDE', tabIndex: 0 },
                            { value: 'PII', label: 'PII', tabIndex: 0 },
                        ]}
                        tagRender={(props: any) => {
                            const { label, closable, value } = props;
                            return (
                                <Tag
                                    key={value}
                                    closable={closable}
                                    onClose={() => handleTagClose(value)}
                                    style={{ marginRight: 3 }}
                                >
                                    {label}
                                </Tag>
                            );
                        }}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
